import { useState, useEffect, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  Clock, CheckCircle, Plus, Edit2, Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Updated event types: merged Livraison + Pose
const EVENT_TYPES = [
  { value: 'mesure', label: 'Relevé de mesures', color: '#3B82F6' },
  { value: 'livraison_pose', label: 'Livraison & Pose', color: '#10B981' },
  { value: 'autre', label: 'Autre', color: '#8B5CF6' },
];

const STATUS_LABELS = {
  proposed: 'En attente',
  validated: 'Confirmée',
  modified_by_admin: 'Modifiée'
};

export default function DashboardPlanning() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [quotes, setQuotes] = useState([]);
  
  // Add Event Dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newEventForm, setNewEventForm] = useState({
    quote_id: '',
    event_type: 'mesure',
    event_label: '',
    proposed_date: '',
    proposed_time: '',
    comment: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await axios.get(
        `${API_URL}/planning/events?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`
      );
      setEvents(response.data);
    } catch (error) {
      // Silent fail — empty events state already handled in UI
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      // Silent fail
    }
  };

  const handleAddEvent = async () => {
    // Validation for "Autre" type
    if (newEventForm.event_type === 'autre' && !newEventForm.event_label.trim()) {
      toast.error('Veuillez préciser le type d\'événement');
      return;
    }
    
    if (!newEventForm.event_type || !newEventForm.proposed_date || !newEventForm.quote_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      // Combine date and time
      let proposed_date = newEventForm.proposed_date;
      if (newEventForm.proposed_time) {
        proposed_date = `${newEventForm.proposed_date}T${newEventForm.proposed_time}:00`;
      }
      
      await axios.post(`${API_URL}/planning/events`, {
        quote_id: newEventForm.quote_id,
        event_type: newEventForm.event_type,
        event_label: newEventForm.event_label,
        proposed_date,
        comment: newEventForm.comment
      });
      
      toast.success('Date proposée. En attente de validation par GRANIPAL.');
      fetchEvents();
      setIsAddDialogOpen(false);
      setNewEventForm({
        quote_id: '',
        event_type: 'mesure',
        event_label: '',
        proposed_date: '',
        proposed_time: '',
        comment: ''
      });
    } catch (error) {
      toast.error('Erreur lors de la proposition de date');
    }
  };

  // Calendar helpers
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Monday start
    const startDay = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [currentDate]);

  const getEventsForDay = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const eventDate = e.validated_date || e.proposed_date;
      return eventDate?.split('T')[0] === dateStr;
    });
  };

  const getEventTypeInfo = (event) => {
    const type = event.event_type === 'pose' || event.event_type === 'livraison' 
      ? 'livraison_pose' 
      : event.event_type;
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[2];
  };

  const getEventDisplayLabel = (event) => {
    if (event.event_type === 'autre' && event.event_label) {
      return event.event_label;
    }
    const typeInfo = getEventTypeInfo(event);
    return typeInfo.label;
  };

  const getEventTime = (event) => {
    const dateStr = event.validated_date || event.proposed_date;
    if (dateStr?.includes('T')) {
      return dateStr.split('T')[1]?.substring(0, 5);
    }
    return null;
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Group events by status
  const pendingEvents = events.filter(e => e.status === 'proposed');
  const validatedEvents = events.filter(e => e.status === 'validated' || e.status === 'modified_by_admin');

  return (
    <div className="space-y-6" data-testid="dashboard-planning">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Mon Planning</h1>
          <p className="text-white/50 mt-1">Visualisez vos relevés et poses planifiés</p>
        </div>
        <button
          onClick={() => setIsAddDialogOpen(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="propose-date-btn"
        >
          <Plus className="w-4 h-4" />
          Proposer une date
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center p-4 bg-[#1C1C1C] border border-white/10">
        {EVENT_TYPES.map(type => (
          <div key={type.value} className="flex items-center gap-1 text-xs text-white/50">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
            {type.label}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        /* Layout: Calendar on left, Events on right */
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar - Takes 2 columns */}
          <div className="lg:col-span-2">
            {/* Calendar Header */}
            <div className="flex items-center justify-between p-4 bg-[#1C1C1C] border border-white/10 mb-0">
              <button onClick={prevMonth} className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-bold text-white">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button onClick={nextMonth} className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="bg-[#1C1C1C] border border-white/10 border-t-0 overflow-hidden">
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-white/10">
                {dayNames.map(day => (
                  <div key={day} className="p-2 text-center text-white/50 text-xs font-medium">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Days grid */}
              <div className="grid grid-cols-7">
                {daysInMonth.map((day, index) => {
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const isToday = day && day.toDateString() === new Date().toDateString();
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-24 p-1 border-b border-r border-white/5 ${
                        !day ? 'bg-white/5' : ''
                      } ${isToday ? 'bg-[#D4AF37]/5' : ''}`}
                    >
                      {day && (
                        <>
                          <div className={`text-xs mb-1 ${
                            isToday ? 'text-[#D4AF37] font-bold' : 'text-white/50'
                          }`}>
                            {day.getDate()}
                          </div>
                          <div className="space-y-0.5">
                            {dayEvents.slice(0, 2).map(event => {
                              const typeInfo = getEventTypeInfo(event);
                              const timeStr = getEventTime(event);
                              return (
                                <Link
                                  key={event.id}
                                  to={`/dashboard/devis/${event.quote_id}`}
                                  className="block w-full text-left p-0.5 rounded text-[10px] truncate hover:opacity-80 transition"
                                  style={{ backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}
                                >
                                  {timeStr && <span className="font-bold">{timeStr} </span>}
                                  <span className="font-medium">{getEventDisplayLabel(event)}</span>
                                </Link>
                              );
                            })}
                            {dayEvents.length > 2 && (
                              <div className="text-[10px] text-white/40 text-center">
                                +{dayEvents.length - 2}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar: Events */}
          <div className="space-y-4">
            {/* Pending events */}
            <div className="card-dashboard max-h-[350px] overflow-y-auto">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2 sticky top-0 bg-[#1C1C1C] py-1">
                <Clock className="w-4 h-4 text-yellow-500" />
                En attente de validation ({pendingEvents.length})
              </h3>
              {pendingEvents.length === 0 ? (
                <p className="text-white/40 text-center py-4 text-sm">Aucune date en attente</p>
              ) : (
                <div className="space-y-2">
                  {pendingEvents.map(event => {
                    const typeInfo = getEventTypeInfo(event);
                    const timeStr = getEventTime(event);
                    return (
                      <Link 
                        key={event.id}
                        to={`/dashboard/devis/${event.quote_id}`}
                        className="block p-2 bg-[#0A0A0A] border border-white/10 hover:border-white/20 transition"
                      >
                        <div className="flex items-start gap-2">
                          <div 
                            className="w-1.5 h-8 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: typeInfo.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{getEventDisplayLabel(event)}</p>
                            <p className="text-white/50 text-xs truncate">{event.client_name}</p>
                            <p className="text-yellow-400 text-xs">
                              {new Date(event.proposed_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                              {timeStr && ` à ${timeStr}`}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Validated events */}
            <div className="card-dashboard max-h-[350px] overflow-y-auto">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2 sticky top-0 bg-[#1C1C1C] py-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Dates confirmées ({validatedEvents.length})
              </h3>
              {validatedEvents.length === 0 ? (
                <p className="text-white/40 text-center py-4 text-sm">Aucune date confirmée</p>
              ) : (
                <div className="space-y-2">
                  {validatedEvents.map(event => {
                    const typeInfo = getEventTypeInfo(event);
                    const timeStr = getEventTime(event);
                    return (
                      <Link 
                        key={event.id}
                        to={`/dashboard/devis/${event.quote_id}`}
                        className="block p-2 bg-[#0A0A0A] border border-white/10 hover:border-white/20 transition"
                      >
                        <div className="flex items-start gap-2">
                          <div 
                            className="w-1.5 h-8 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: typeInfo.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{getEventDisplayLabel(event)}</p>
                            <p className="text-white/50 text-xs truncate">{event.client_name}</p>
                            <p className="text-green-400 text-xs">
                              {new Date(event.validated_date || event.proposed_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                              {timeStr && ` à ${timeStr}`}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Event Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Proposer une date</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Dossier concerné *</label>
              <Select 
                value={newEventForm.quote_id} 
                onValueChange={(v) => setNewEventForm({...newEventForm, quote_id: v})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue placeholder="Sélectionner un dossier" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10 max-h-60">
                  {quotes.map(quote => (
                    <SelectItem key={quote.id} value={quote.id} className="text-white hover:bg-white/10">
                      {quote.quote_number} - {quote.client_info?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Type d'événement *</label>
              <Select 
                value={newEventForm.event_type} 
                onValueChange={(v) => setNewEventForm({...newEventForm, event_type: v, event_label: ''})}
              >
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/10">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newEventForm.event_type === 'autre' && (
              <div>
                <label className="text-white/60 text-sm mb-2 block">Préciser le type *</label>
                <Input
                  value={newEventForm.event_label}
                  onChange={(e) => setNewEventForm({...newEventForm, event_label: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  placeholder="Ex: SAV, Contrôle chantier..."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                  Date souhaitée *
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={newEventForm.proposed_date}
                    onChange={(e) => setNewEventForm({...newEventForm, proposed_date: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  Heure souhaitée
                </label>
                <div className="relative">
                  <Input
                    type="time"
                    value={newEventForm.proposed_time}
                    onChange={(e) => setNewEventForm({...newEventForm, proposed_time: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none pl-10"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Commentaire</label>
              <Input
                value={newEventForm.comment}
                onChange={(e) => setNewEventForm({...newEventForm, comment: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                placeholder="Précisions éventuelles..."
              />
            </div>

            <p className="text-white/40 text-xs">
              La date proposée sera soumise à validation par GRANIPAL.
            </p>

            <div className="flex gap-3 pt-4">
              <button onClick={() => setIsAddDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button onClick={handleAddEvent} className="btn-primary flex-1">
                Proposer cette date
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
